/**
 * Campaign Store (Armory) — DOM modal for purchasing weapons, ammo, and items.
 */
import type { CampaignState, WeaponKey, ItemKey } from '../types';
import { WEAPONS, WEAPON_KEYS, CAMPAIGN_WEAPON_PRICES, HEAL_ITEMS } from '../constants';
import { cur, isFlavorEnabled } from './flavor';
import { isItemUnlocked } from '../core/campaignGear';
import { theme } from './theme';
import { thumbnails, thumbCanvasHtml } from '../rendering/modelThumbnails';

// =============================================================================
// MODULE STATE
// =============================================================================

let modalEl: HTMLDivElement | null = null;
let contentEl: HTMLDivElement | null = null;
let headerMoneyEl: HTMLSpanElement | null = null;
let activeTab: 'weapons' | 'ammo' | 'items' = 'weapons';

let currentState: CampaignState | null = null;
let currentCallbacks: {
  onPurchaseWeapon: (weapon: WeaponKey) => void;
  onPurchaseAmmo: (weapon: WeaponKey, qty: number) => void;
  onPurchaseItem: (item: ItemKey, qty: number) => void;
  onClose: () => void;
} | null = null;

// =============================================================================
// SHOW / HIDE / UPDATE
// =============================================================================

export function showCampaignStore(
  state: CampaignState,
  callbacks: {
    onPurchaseWeapon: (weapon: WeaponKey) => void;
    onPurchaseAmmo: (weapon: WeaponKey, qty: number) => void;
    onPurchaseItem: (item: ItemKey, qty: number) => void;
    onClose: () => void;
  }
): void {
  currentState = state;
  currentCallbacks = callbacks;
  activeTab = 'weapons';

  if (!modalEl) {
    createModal();
  }

  modalEl!.style.display = 'flex';
  renderContent();
}

export function updateCampaignStore(state: CampaignState): void {
  currentState = state;
  if (!modalEl || modalEl.style.display === 'none') return;
  updateHeaderMoney();
  renderContent();
}

export function hideCampaignStore(): void {
  if (modalEl) {
    modalEl.style.display = 'none';
  }
  thumbnails.clear();
}

// =============================================================================
// DOM CREATION
// =============================================================================

function createModal(): void {
  // Backdrop
  modalEl = document.createElement('div');
  modalEl.id = 'campaign-store-modal';
  modalEl.style.cssText = `
    position:fixed; inset:0; z-index:105;
    display:flex; align-items:center; justify-content:center;
    background:${theme.surface.overlay};
    font-family:'Space Grotesk',monospace;
  `;
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) {
      currentCallbacks?.onClose();
    }
  });

  // Panel
  const panel = document.createElement('div');
  panel.style.cssText = `
    width:560px; max-height:80vh; background:${theme.surface.panelBg};
    border:1px solid ${theme.surface.border}; border-radius:10px;
    display:flex; flex-direction:column; overflow:hidden;
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    padding:14px 18px; display:flex; align-items:center; justify-content:space-between;
    border-bottom:1px solid ${theme.goldBorder20};
  `;
  header.innerHTML = `
    <div style="display:flex; align-items:center; gap:16px;">
      <span style="font-size:15px; color:${theme.accent}; font-weight:700; letter-spacing:2px;">ARMORY</span>
      <span id="campaign-store-money" style="font-size:12px; color:${theme.accent};"></span>
    </div>
    <button id="campaign-store-close" style="
      background:none; border:none; color:${theme.text.secondary}; font-size:18px; cursor:pointer;
      padding:4px 8px; line-height:1;
    ">&times;</button>
  `;
  panel.appendChild(header);

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.id = 'campaign-store-tabs';
  tabBar.style.cssText = `
    display:flex; border-bottom:1px solid ${theme.goldBorder15}; flex-shrink:0;
  `;
  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    { key: 'weapons', label: 'WEAPONS' },
    { key: 'ammo', label: 'AMMO' },
    { key: 'items', label: 'ITEMS' },
  ];
  for (const tab of tabs) {
    const btn = document.createElement('button');
    btn.dataset.tab = tab.key;
    btn.textContent = tab.label;
    btn.style.cssText = `
      flex:1; padding:10px 0; background:none; border:none; border-bottom:2px solid transparent;
      color:${theme.text.secondary}; font-family:'Space Grotesk',monospace; font-size:11px;
      cursor:pointer; letter-spacing:1px; transition:color 0.15s, border-color 0.15s;
    `;
    btn.addEventListener('click', () => {
      activeTab = tab.key as typeof activeTab;
      renderContent();
    });
    tabBar.appendChild(btn);
  }
  panel.appendChild(tabBar);

  // Content area
  contentEl = document.createElement('div');
  contentEl.style.cssText = `
    flex:1; overflow-y:auto; padding:14px 18px; min-height:200px;
  `;
  panel.appendChild(contentEl);

  modalEl.appendChild(panel);
  document.body.appendChild(modalEl);

  // Wire close button
  document.getElementById('campaign-store-close')?.addEventListener('click', () => {
    currentCallbacks?.onClose();
  });

  headerMoneyEl = document.getElementById('campaign-store-money') as HTMLSpanElement;
}

// =============================================================================
// RENDERING
// =============================================================================

function updateHeaderMoney(): void {
  if (headerMoneyEl && currentState) {
    headerMoneyEl.textContent = `${currentState.playerMoney} ${cur()}`;
  }
}

function renderContent(): void {
  if (!contentEl || !currentState) return;
  updateHeaderMoney();
  updateTabStyles();

  switch (activeTab) {
    case 'weapons': renderWeaponsTab(); break;
    case 'ammo': renderAmmoTab(); break;
    case 'items': renderItemsTab(); break;
  }
}

function updateTabStyles(): void {
  const tabBar = document.getElementById('campaign-store-tabs');
  if (!tabBar) return;
  for (const btn of tabBar.children) {
    const el = btn as HTMLButtonElement;
    const isActive = el.dataset.tab === activeTab;
    el.style.color = isActive ? theme.accent : theme.text.secondary;
    el.style.borderBottomColor = isActive ? theme.accent : 'transparent';
  }
}

function renderWeaponsTab(): void {
  if (!contentEl || !currentState) return;
  const inv = currentState.inventory;

  let html = '<div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">';

  for (const key of WEAPON_KEYS) {
    const weapon = WEAPONS[key];
    const owned = inv.ownedWeapons.has(key);
    const price = CAMPAIGN_WEAPON_PRICES[key];
    const isStandard = key === 'standard' || key === 'probe';
    const canAfford = price !== undefined && currentState.playerMoney >= price;

    html += `<div style="
      background:${theme.rowBg}; border:1px solid ${owned ? theme.surface.border : theme.surface.borderSubtle};
      border-radius:8px; padding:12px; display:flex; flex-direction:column; gap:6px;
    ">`;

    // Model + name
    html += `<div style="display:flex; align-items:center; gap:10px;">
      ${thumbCanvasHtml(`weapon:${key}`, 56, `background:${theme.chipBg}; border-radius:6px; flex-shrink:0;`)}
      <div>
        <div style="font-size:12px; color:${theme.text.primary}; font-weight:600;">${weapon.name}</div>
        ${isFlavorEnabled() ? `<div style="font-size:10px; color:${theme.text.secondary}; font-style:italic;">${weapon.flavorName}</div>` : ''}
      </div>
    </div>`;

    // Price / status
    if (isStandard) {
      html += `<div style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; text-align:center; padding:4px;">STANDARD ISSUE</div>`;
    } else if (owned) {
      html += `<div style="
        font-size:10px; color:${theme.accent}; letter-spacing:1px; text-align:center;
        padding:4px; background:${theme.goldBg10}; border-radius:4px;
      ">OWNED</div>`;
    } else {
      html += `<button data-buy-weapon="${key}" style="
        width:100%; padding:6px; border:1px solid ${canAfford ? theme.accent : theme.text.disabled};
        background:${canAfford ? theme.goldBg12 : 'transparent'};
        color:${canAfford ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
        font-size:11px; cursor:${canAfford ? 'pointer' : 'default'}; border-radius:4px;
        ${canAfford ? '' : 'opacity:0.5;'}
      ">BUY — ${price} ${cur()}</button>`;
    }

    html += '</div>';
  }

  html += '</div>';
  contentEl.innerHTML = html;
  thumbnails.mount(contentEl);

  // Wire buy buttons
  contentEl.querySelectorAll('[data-buy-weapon]').forEach(btn => {
    btn.addEventListener('click', () => {
      const weapon = (btn as HTMLElement).dataset.buyWeapon as WeaponKey;
      currentCallbacks?.onPurchaseWeapon(weapon);
    });
  });
}

function renderAmmoTab(): void {
  if (!contentEl || !currentState) return;
  const inv = currentState.inventory;

  // Only show owned weapons (excluding standard which is unlimited)
  const ownedWeapons = [...inv.ownedWeapons].filter(w => w !== 'standard');

  if (ownedWeapons.length === 0) {
    contentEl.innerHTML = `<div style="text-align:center; color:${theme.text.secondary}; padding:40px; font-size:12px;">
      No weapons owned yet. Purchase weapons in the WEAPONS tab first.
    </div>`;
    return;
  }

  let html = '';
  for (const key of ownedWeapons) {
    const weapon = WEAPONS[key];
    const stock = inv.ammoStock[key] ?? 0;
    const costPer = weapon.cbpCost;
    const canAfford = currentState.playerMoney >= costPer;

    html += `<div style="
      display:flex; align-items:center; justify-content:space-between;
      padding:10px 12px; border-bottom:1px solid ${theme.surface.borderDim};
    ">
      <div style="display:flex; align-items:center; gap:10px;">
        <span style="font-size:16px;">${weapon.icon}</span>
        <div>
          <div style="font-size:12px; color:${theme.text.primary};">${weapon.name}</div>
          <div style="font-size:10px; color:${theme.text.secondary};">${costPer} ${cur()}/shot</div>
        </div>
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <span style="font-size:13px; color:${theme.accent}; min-width:24px; text-align:center;">${stock}</span>
        <button data-buy-ammo="${key}" data-qty="1" style="
          padding:4px 10px; border:1px solid ${canAfford ? theme.accent : theme.text.disabled};
          background:${canAfford ? theme.goldBg12 : 'transparent'};
          color:${canAfford ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
          font-size:11px; cursor:${canAfford ? 'pointer' : 'default'}; border-radius:3px;
          ${canAfford ? '' : 'opacity:0.5;'}
        ">+1</button>
        <button data-buy-ammo="${key}" data-qty="5" style="
          padding:4px 10px; border:1px solid ${currentState.playerMoney >= costPer * 5 ? theme.accent : theme.text.disabled};
          background:${currentState.playerMoney >= costPer * 5 ? theme.goldBg12 : 'transparent'};
          color:${currentState.playerMoney >= costPer * 5 ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
          font-size:11px; cursor:${currentState.playerMoney >= costPer * 5 ? 'pointer' : 'default'}; border-radius:3px;
          ${currentState.playerMoney >= costPer * 5 ? '' : 'opacity:0.5;'}
        ">+5</button>
      </div>
    </div>`;
  }

  contentEl.innerHTML = html;

  contentEl.querySelectorAll('[data-buy-ammo]').forEach(btn => {
    btn.addEventListener('click', () => {
      const el = btn as HTMLElement;
      const weapon = el.dataset.buyAmmo as WeaponKey;
      const qty = parseInt(el.dataset.qty!, 10);
      currentCallbacks?.onPurchaseAmmo(weapon, qty);
    });
  });
}

function renderItemsTab(): void {
  if (!contentEl || !currentState) return;
  const inv = currentState.inventory;

  let html = '';
  const itemKeys: ItemKey[] = ['healSmall', 'healLarge'];

  for (const key of itemKeys) {
    const item = HEAL_ITEMS[key];
    const stock = inv.items[key] ?? 0;
    const unlocked = isItemUnlocked(currentState.inventory.gearUpgrades, key);
    const canAfford = currentState.playerMoney >= item.cbpCost;

    html += `<div style="
      display:flex; align-items:center; justify-content:space-between;
      padding:12px; border-bottom:1px solid ${theme.surface.borderDim};
      ${unlocked ? '' : 'opacity:0.45;'}
    ">
      <div style="display:flex; align-items:center; gap:10px;">
        ${thumbCanvasHtml(`item:${key}`, 44, `background:${theme.chipBg}; border-radius:6px; flex-shrink:0;`)}
        <div>
          <div style="font-size:12px; color:${theme.text.primary};">${item.name}</div>
          ${isFlavorEnabled() ? `<div style="font-size:10px; color:${theme.text.secondary}; font-style:italic;">${item.flavorName}</div>` : ''}
          <div style="font-size:10px; color:${theme.text.secondary};">${item.healPercent}% HP — ${item.cbpCost} ${cur()}</div>
        </div>
      </div>`;

    if (!unlocked) {
      html += `<div style="font-size:10px; color:${theme.text.muted}; letter-spacing:1px; text-align:right;">
        LOCKED<br><span style="font-size:9px;">Unlock in GEAR</span>
      </div>`;
    } else {
      html += `<div style="display:flex; align-items:center; gap:8px;">
        <span style="font-size:13px; color:${theme.accent}; min-width:24px; text-align:center;">${stock}</span>
        <button data-buy-item="${key}" data-qty="1" style="
          padding:4px 10px; border:1px solid ${canAfford ? theme.accent : theme.text.disabled};
          background:${canAfford ? theme.goldBg12 : 'transparent'};
          color:${canAfford ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
          font-size:11px; cursor:${canAfford ? 'pointer' : 'default'}; border-radius:3px;
          ${canAfford ? '' : 'opacity:0.5;'}
        ">+1</button>
        <button data-buy-item="${key}" data-qty="3" style="
          padding:4px 10px; border:1px solid ${currentState.playerMoney >= item.cbpCost * 3 ? theme.accent : theme.text.disabled};
          background:${currentState.playerMoney >= item.cbpCost * 3 ? theme.goldBg12 : 'transparent'};
          color:${currentState.playerMoney >= item.cbpCost * 3 ? theme.accent : theme.text.disabled}; font-family:'Space Grotesk',monospace;
          font-size:11px; cursor:${currentState.playerMoney >= item.cbpCost * 3 ? 'pointer' : 'default'}; border-radius:3px;
          ${currentState.playerMoney >= item.cbpCost * 3 ? '' : 'opacity:0.5;'}
        ">+3</button>
      </div>`;
    }

    html += '</div>';
  }

  contentEl.innerHTML = html;
  thumbnails.mount(contentEl);

  contentEl.querySelectorAll('[data-buy-item]').forEach(btn => {
    btn.addEventListener('click', () => {
      const el = btn as HTMLElement;
      const item = el.dataset.buyItem as ItemKey;
      const qty = parseInt(el.dataset.qty!, 10);
      currentCallbacks?.onPurchaseItem(item, qty);
    });
  });
}

